// client/src/components/approvals/ApprovalsPage.jsx
//
// /:tenant/approvals — team approvals inbox (Growth Phase 10, feature_key
// "team_approvals"). Three blocks:
//   - Approval settings: whether posts need approval and who approves
//     (editable by owner / admin; read-only for everyone else);
//   - Waiting for your review: posts in review, for approvers only;
//   - Your submissions: your posts that are in review or came back.
// Approve / request changes / reject reuse ContentApprovalPanel, so the
// state machine and the review history stay in one place.
//
// API: /api/approvals/:tenant (server/routes/approvalRoutes.js) and the
// review actions on /api/schedule/:postId/* (scheduledPostRoutes.js).

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft, FaHome } from 'react-icons/fa';
import AppFooter from '../AppFooter';
import LoadingState from '../common/LoadingState';
import ErrorState from '../common/ErrorState';
import ContentApprovalPanel from '../campaign/ContentApprovalPanel';
import {
  getApprovalPolicy, getApprovalsInbox, saveApprovalPolicy,
} from '../../services/contentApprovalApi';
import '../analytics/PerformancePanel.css';
import '../forms/Forms.css';
import './Approvals.css';

const POLICY_EDITORS = ['owner', 'admin'];

export function approverRoleLabel(t, role) {
  return {
    manager: t('approvals.policy.role.manager', 'Managers, admins and the owner'),
    admin: t('approvals.policy.role.admin', 'Admins and the owner'),
    owner: t('approvals.policy.role.owner', 'Only the owner'),
  }[role] || role;
}

function formatWhen(value) {
  if (!value) return null;
  const d = new Date(String(value).replace(' ', 'T') + (String(value).includes('T') ? '' : 'Z'));
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
}

function PostSummary({ item, t }) {
  const when = formatWhen(item.scheduledOn);
  return (
    <div className="ap-summary">
      <p className="ap-content">{item.content || t('approvals.item.noText', '(No text, media only)')}</p>
      <div className="ap-meta">
        {item.platforms.length > 0 && <span>{item.platforms.join(', ')}</span>}
        {item.mediaCount > 0 && <span>{t('approvals.item.media', '{{count}} media file(s)', { count: item.mediaCount })}</span>}
        <span>{when ? t('approvals.item.scheduled', 'Scheduled {{when}}', { when }) : t('approvals.item.asap', 'Publishes as soon as it is approved')}</span>
        {item.submittedBy?.name && <span>{t('approvals.item.by', 'Submitted by {{name}}', { name: item.submittedBy.name })}</span>}
      </div>
      {item.lastNote && item.approvalStatus !== 'in_review' && (
        <div className="note warn ap-note">{t('approvals.item.note', 'Reviewer note: {{note}}', { note: item.lastNote })}</div>
      )}
    </div>
  );
}

export default function ApprovalsPage({ tenantId: tenantProp }) {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const tenant = Number(tenantProp || params.tenant);

  const [policy, setPolicy] = useState(null);
  const [draft, setDraft] = useState(null);
  const [inbox, setInbox] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      const [p, i] = await Promise.all([getApprovalPolicy(tenant), getApprovalsInbox(tenant)]);
      setPolicy(p.data);
      setDraft({ required: p.data.required, approverMinRole: p.data.approverMinRole });
      setInbox(i.data);
    } catch {
      setError(t('approvals.loadError', 'Could not load approvals.'));
    }
  }, [tenant, t]);

  useEffect(() => { load(); }, [load]);

  const canEditPolicy = policy && POLICY_EDITORS.includes(policy.role);

  const save = async (e) => {
    e.preventDefault();
    setNotice(null);
    setSaving(true);
    try {
      const res = await saveApprovalPolicy(tenant, draft);
      setPolicy(res.data);
      setDraft({ required: res.data.required, approverMinRole: res.data.approverMinRole });
      setNotice({ kind: 'ok', text: t('approvals.policy.saved', 'Saved.') });
      const i = await getApprovalsInbox(tenant);
      setInbox(i.data);
    } catch (err) {
      setNotice({ kind: 'error', text: err?.response?.data?.error || t('approvals.policy.saveError', 'Could not save the approval settings.') });
    } finally {
      setSaving(false);
    }
  };

  const refresh = async () => {
    try {
      const i = await getApprovalsInbox(tenant);
      setInbox(i.data);
    } catch { /* keep the last list */ }
  };

  return (
    <div className="ap-page">
      <section className="surface">
        <h2>{t('approvals.title', 'Approvals')}</h2>
        <p className="surface-muted perf-small">
          {t('approvals.intro', 'Social posts that need a second look before they publish. A post only goes out once it is approved; sending it back or rejecting it stops anything already queued.')}
        </p>
      </section>

      {error && <ErrorState message={error} onRetry={load} />}
      {!error && (!policy || !inbox) && <LoadingState />}

      {policy && draft && (
        <section className="surface" aria-labelledby="ap-policy-title">
          <h4 id="ap-policy-title">{t('approvals.policy.title', 'Approval settings')}</h4>
          {canEditPolicy ? (
            <form className="fo-form-grid" onSubmit={save}>
              <label className="fo-check ap-span">
                <input
                  type="checkbox"
                  checked={draft.required}
                  onChange={(e) => setDraft({ ...draft, required: e.target.checked })}
                />
                {t('approvals.policy.required', 'Posts need approval before they publish')}
              </label>
              <label className="fo-field">
                {t('approvals.policy.who', 'Who approves')}
                <select
                  value={draft.approverMinRole}
                  disabled={!policy.approverMinRoleConfigurable}
                  onChange={(e) => setDraft({ ...draft, approverMinRole: e.target.value })}
                >
                  {(policy.approverMinRoles || ['manager', 'admin', 'owner']).map((r) => (
                    <option key={r} value={r}>{approverRoleLabel(t, r)}</option>
                  ))}
                </select>
              </label>
              <div className="fo-field" style={{ justifyContent: 'flex-end' }}>
                <button type="submit" className="lm-btn" disabled={saving}>
                  {t('approvals.policy.save', 'Save settings')}
                </button>
              </div>
            </form>
          ) : (
            <p className="surface-muted perf-small">
              {policy.required
                ? t('approvals.policy.readRequired', 'Posts need approval before they publish. Approvers: {{who}}.', { who: approverRoleLabel(t, policy.approverMinRole) })
                : t('approvals.policy.readOff', 'Posts publish without approval. You can still submit a post for review.')}
            </p>
          )}
          {canEditPolicy && !policy.approverMinRoleConfigurable && (
            <div className="note info">{t('approvals.policy.roleNotReady', 'Choosing who approves needs a database update. Until then, managers, admins and the owner approve.')}</div>
          )}
          {policy.mustSubmit && (
            <div className="note info">{t('approvals.policy.youSubmit', 'Your posts go to an approver before they publish.')}</div>
          )}
          {notice && <div className={`note ${notice.kind}`} role={notice.kind === 'error' ? 'alert' : 'status'}>{notice.text}</div>}
        </section>
      )}

      {inbox && !inbox.ready && (
        <div className="note warn" role="status">
          {t('approvals.notReady', 'Content approval needs a database update before it can be used.')}
        </div>
      )}

      {inbox && inbox.ready && inbox.canApprove && (
        <section className="surface" aria-labelledby="ap-waiting-title">
          <h4 id="ap-waiting-title">
            {t('approvals.waiting.title', 'Waiting for your review')}
            {inbox.waiting.length > 0 && <span className="lm-badge ap-count">{inbox.waiting.length}</span>}
          </h4>
          {inbox.waiting.length === 0 && (
            <p className="surface-muted perf-small">{t('approvals.waiting.empty', 'Nothing is waiting for review.')}</p>
          )}
          <ul className="ap-list">
            {inbox.waiting.map((item) => (
              <li key={item.id} className="ap-item">
                <PostSummary item={item} t={t} />
                <ContentApprovalPanel
                  tenantId={tenant}
                  canApprove
                  post={{ id: item.id, approval_status: item.approvalStatus }}
                  onChanged={refresh}
                />
              </li>
            ))}
          </ul>
        </section>
      )}

      {inbox && inbox.ready && (
        <section className="surface" aria-labelledby="ap-mine-title">
          <h4 id="ap-mine-title">{t('approvals.mine.title', 'Your submissions')}</h4>
          {inbox.mine.length === 0 && (
            <p className="surface-muted perf-small">{t('approvals.mine.empty', 'You have no posts in review or sent back.')}</p>
          )}
          <ul className="ap-list">
            {inbox.mine.map((item) => (
              <li key={item.id} className="ap-item">
                <PostSummary item={item} t={t} />
                <ContentApprovalPanel
                  tenantId={tenant}
                  canApprove={Boolean(inbox.canApprove)}
                  post={{ id: item.id, approval_status: item.approvalStatus }}
                  onChanged={refresh}
                />
              </li>
            ))}
          </ul>
        </section>
      )}

      <AppFooter
        buttons={[
          { icon: <FaArrowLeft />, label: t('common.back', 'Back'), onClick: () => navigate(-1) },
          { icon: <FaHome />, label: t('common.home', 'Home'), onClick: () => navigate(`/${tenant}/dashboard`) },
        ]}
      />
    </div>
  );
}
